import React, { useState, useMemo } from "react";
import { Chip } from "@mui/material";
import {
  APPLICATION_STATUS_CONFIG,
  APPLICATION_REJECTION_REASONS,
} from "./StatusControl/configs/applicationConfig";
import type {
  ApplicationStatus,
  ApplicationStatusHistoryEntry,
} from "../types/shared.types";

interface ApplicationStatusHistoryProps {
  history: ApplicationStatusHistoryEntry[];
}

const ApplicationStatusHistory: React.FC<ApplicationStatusHistoryProps> = ({
  history,
}) => {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dateFrom, setDateFrom] = useState<string>("");
  const [dateTo, setDateTo] = useState<string>("");

  const statusOptions = useMemo(() => {
    const statuses = new Set<string>();
    for (const entry of history) {
      if (entry.to_status) statuses.add(entry.to_status);
    }
    return Array.from(statuses);
  }, [history]);

  const filteredHistory = useMemo(() => {
    let result = history;

    if (statusFilter !== "all") {
      result = result.filter((entry) => entry.to_status === statusFilter);
    }

    if (dateFrom) {
      const from = new Date(dateFrom);
      result = result.filter((entry) => new Date(entry.created_at) >= from);
    }

    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      result = result.filter((entry) => new Date(entry.created_at) <= to);
    }

    return result;
  }, [history, statusFilter, dateFrom, dateTo]);

  const hasFilters = statusFilter !== "all" || dateFrom || dateTo;

  return (
    <div className="history-tab">
      <h3>Status Change History</h3>

      <div
        className="history-filters"
        style={{
          display: "flex",
          gap: "12px",
          marginBottom: "16px",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            fontSize: "13px",
          }}
        >
          <option value="all">All statuses</option>
          {statusOptions.map((status) => {
            const config =
              APPLICATION_STATUS_CONFIG[status as ApplicationStatus];
            return (
              <option key={status} value={status}>
                {config?.label || status}
              </option>
            );
          })}
        </select>

        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          placeholder="From"
          title="From date"
          style={{
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            fontSize: "13px",
          }}
        />

        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          placeholder="To"
          title="To date"
          style={{
            padding: "6px 10px",
            borderRadius: "6px",
            border: "1px solid #d1d5db",
            fontSize: "13px",
          }}
        />

        {hasFilters && (
          <button
            onClick={() => {
              setStatusFilter("all");
              setDateFrom("");
              setDateTo("");
            }}
            style={{
              padding: "6px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              background: "transparent",
              fontSize: "13px",
              cursor: "pointer",
              color: "#6b7280",
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {filteredHistory.length === 0 ? (
        <p className="empty-state">
          {hasFilters
            ? "No status changes match the selected filters"
            : "No status changes recorded"}
        </p>
      ) : (
        <div className="history-list">
          {filteredHistory.map((entry) => {
            const fromConfig = entry.from_status
              ? APPLICATION_STATUS_CONFIG[entry.from_status]
              : null;
            const toConfig = APPLICATION_STATUS_CONFIG[entry.to_status];
            return (
              <div key={entry.id} className="history-entry">
                <div className="history-entry-header">
                  <div className="history-transition">
                    {entry.from_status ? (
                      <>
                        <Chip
                          label={fromConfig?.label || entry.from_status}
                          size="small"
                          sx={{
                            backgroundColor: fromConfig?.bgColor || "#f3f4f6",
                            color: fromConfig?.color || "#374151",
                            fontSize: "11px",
                          }}
                        />
                        <span className="history-arrow">&rarr;</span>
                      </>
                    ) : (
                      <span className="history-initial">Created as</span>
                    )}
                    <Chip
                      label={toConfig?.label || entry.to_status}
                      size="small"
                      sx={{
                        backgroundColor: toConfig?.bgColor || "#f3f4f6",
                        color: toConfig?.color || "#374151",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    />
                  </div>
                  <div className="history-meta">
                    <span className="history-user">
                      {entry.changed_by_name || "System"}
                    </span>
                    <span className="history-date">
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
                {entry.note && <p className="history-note">{entry.note}</p>}
                {entry.rejection_reason && (
                  <div className="history-rejection">
                    <strong>Rejection:</strong>{" "}
                    {APPLICATION_REJECTION_REASONS[entry.rejection_reason]
                      ?.label || entry.rejection_reason}
                    {entry.rejected_from_status && (
                      <span>
                        {" "}
                        (from{" "}
                        {APPLICATION_STATUS_CONFIG[entry.rejected_from_status]
                          ?.label || entry.rejected_from_status}
                        )
                      </span>
                    )}
                    {entry.rejection_reason_note && (
                      <p>{entry.rejection_reason_note}</p>
                    )}
                  </div>
                )}
                {entry.override_justification && (
                  <div className="history-override">
                    <strong>Override Justification:</strong>{" "}
                    {entry.override_justification}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ApplicationStatusHistory;
